import { createClient } from '@supabase/supabase-js';
import { Capacitor } from '@capacitor/core';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

/**
 * Which build is talking, on every request.
 *
 * "4.3.9 (105) / OTA 102 / 202609180913": the marketing version, the native
 * build, the OTA number, the web bundle stamp. It travels as the x-app-version
 * header and a trigger on the tables that care (`public.cards`,
 * `public.scan_timings`) writes it into the row, so no insert path has to
 * remember to send it and none can forget. When somebody reports a card that
 * saved wrong, the row says what they were running.
 *
 * The native build is only known once the App plugin answers, which is after
 * this client exists, so the header reads a variable rather than a constant.
 */
const webVersion = `${process.env.REACT_APP_VERSION || '0.0.0'} (web) / OTA ${process.env.REACT_APP_OTA_NUMBER || 0} / ${process.env.REACT_APP_BUILD_STAMP || 0}`;
let appVersion = webVersion;

/** The version string every request currently carries. */
export const currentAppVersion = () => appVersion;

/** Called once the native shell has said which build it is. */
export function setNativeBuild(info) {
  if (!info?.version) return;
  appVersion = `${info.version} (${info.build}) / OTA ${process.env.REACT_APP_OTA_NUMBER || 0} / ${process.env.REACT_APP_BUILD_STAMP || 0}`;
}

const fetchWithVersion = (input, init = {}) => {
  const headers = new Headers(init.headers || (input instanceof Request ? input.headers : undefined));
  headers.set('x-app-version', appVersion);
  return fetch(input, { ...init, headers });
};

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
  },
  global: { fetch: fetchWithVersion },
});

// Ask the shell which build it is, once, as early as the client exists. Until
// it answers the header carries the web version, which is still true.
if (Capacitor.isNativePlatform()) {
  import('@capacitor/app')
    .then(({ App }) => App.getInfo())
    .then(setNativeBuild)
    .catch(() => { /* the web version stays on the header */ });
}
